import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';

import { renderCell, escapeHtml } from '../lib/cells';
import { labelFor } from '../data/field-labels';
import { TABLE_META } from '../data/table-meta';
import type {
  GridColumn,
  SortOrder,
  TableMetaFromServer,
} from '../types';

interface GridData {
  tableName: string;
  rows: Record<string, unknown>[];
  sortCol: string;
  sortOrder: SortOrder;
  loading: boolean;
  errorMessage: string;
}

/**
 * 데이터 그리드 — 헤더 정렬·행 클릭 → 드로어.
 *
 * Emit:
 *  - sort:change   { col, order }
 *  - row:open      { row }
 */
export class DataGrid extends SolaView<GridData> {
  /** AdminApp이 알려주는 현재 테이블의 서버 메타데이터 (sortable 화이트리스트 등) */
  private tableMeta: TableMetaFromServer | null = null;

  override events = {
    click: {
      '@sort': 'handleSort',
      '@row': 'handleRowClick',
    },
  };

  constructor() {
    super();
    this.data = {
      tableName: '',
      rows: [],
      sortCol: '',
      sortOrder: 'asc',
      loading: false,
      errorMessage: '',
    };
  }

  // ===== 외부에서 호출되는 API =====

  setTable(meta: TableMetaFromServer, sortCol: string, sortOrder: SortOrder): void {
    this.tableMeta = meta;
    this.data.tableName = meta.name;
    this.data.sortCol = sortCol;
    this.data.sortOrder = sortOrder;
    this.data.rows = [];
    this.data.errorMessage = '';
    this.rerenderAll();
  }

  setRows(rows: Record<string, unknown>[]): void {
    this.data.rows = rows;
    this.rerenderBody();
  }

  setSort(col: string, order: SortOrder): void {
    this.data.sortCol = col;
    this.data.sortOrder = order;
    this.rerenderHead();
  }

  setLoading(on: boolean): void {
    this.data.loading = on;
    this._rootElement?.classList.toggle('is-loading', on);
  }

  setError(msg: string): void {
    this.data.errorMessage = msg;
    const err = this._rootElement?.querySelector('.grid-error');
    if (err) {
      err.textContent = msg;
      err.classList.toggle('hidden', !msg);
    }
  }

  // ===== 렌더 =====

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <section class="grid-wrap">
        <div class="grid-scroll">
          <table class="grid">
            <thead></thead>
            <tbody></tbody>
          </table>
          <div class="grid-empty hidden">표시할 데이터가 없습니다.</div>
          <div class="grid-loading">
            <div class="spinner"></div>
            <span>불러오는 중…</span>
          </div>
          <div class="grid-error hidden"></div>
        </div>
      </section>
    `;
  }

  private rerenderAll(): void {
    this.rerenderHead();
    this.rerenderBody();
  }

  private cols(): GridColumn[] {
    return TABLE_META[this.data.tableName]?.cols ?? [];
  }

  private rerenderHead(): void {
    const thead = this._rootElement?.querySelector('.grid > thead');
    if (!thead) return;
    const cols = this.cols();
    if (cols.length === 0) {
      thead.innerHTML = '<tr><th>이 테이블의 메타 정의가 없습니다</th></tr>';
      return;
    }
    const sortable = new Set(this.tableMeta?.sortable ?? []);
    const head = cols
      .map((c) => {
        const isSorted = this.data.sortCol === c.key;
        const can = sortable.has(c.key);
        const arrow = isSorted
          ? this.data.sortOrder === 'asc'
            ? '▲'
            : '▼'
          : can
            ? '↕'
            : '';
        // 좁은 컬럼(<= 60px)은 width 까지 명시해 auto-grow 방지.
        // 그 외엔 min-width 만 줘서 콘텐츠에 따라 늘어나게 둠.
        const wAttr = c.w
          ? c.w <= 60
            ? ` style="width:${c.w}px;min-width:${c.w}px"`
            : ` style="min-width:${c.w}px"`
          : '';
        const sortAttr = can ? ` data-action="sort" data-col="${escapeHtml(c.key)}"` : '';
        const typeAttr = ` data-type="${escapeHtml(c.type)}"`;
        const cls = isSorted ? ' class="sorted"' : '';
        return `<th${cls}${sortAttr}${typeAttr}${wAttr}>
          ${escapeHtml(labelFor(c.key))}
          <span class="sort-arrow">${arrow}</span>
        </th>`;
      })
      .join('');
    thead.innerHTML = `<tr>${head}</tr>`;
  }

  private rerenderBody(): void {
    const tbody = this._rootElement?.querySelector('.grid > tbody');
    const empty = this._rootElement?.querySelector('.grid-empty');
    if (!tbody || !empty) return;

    const cols = this.cols();
    if (this.data.rows.length === 0) {
      tbody.innerHTML = '';
      empty.classList.remove('hidden');
      return;
    }
    empty.classList.add('hidden');

    const rowsHtml = this.data.rows
      .map((row, i) => {
        const cells = cols
          .map((c) => {
            const raw = row[c.key];
            return `<td data-col="${escapeHtml(c.key)}">${renderCell(raw, c.type)}</td>`;
          })
          .join('');
        return `<tr data-action="row" data-row="${i}">${cells}</tr>`;
      })
      .join('');
    tbody.innerHTML = rowsHtml;
  }

  // ===== 이벤트 핸들러 =====

  handleSort(_e: Event, target: HTMLElement): void {
    const col = target.closest<HTMLElement>('[data-col]')?.dataset.col;
    if (!col) return;
    let nextOrder: SortOrder = 'asc';
    if (this.data.sortCol === col) {
      nextOrder = this.data.sortOrder === 'asc' ? 'desc' : 'asc';
    }
    this.emit('sort:change', { col, order: nextOrder });
  }

  handleRowClick(_e: Event, target: HTMLElement): void {
    const idx = Number(target.closest<HTMLElement>('[data-row]')?.dataset.row);
    if (!Number.isFinite(idx)) return;
    const row = this.data.rows[idx];
    if (!row) return;
    this.emit('row:open', { row });
  }
}
